import { ReactNode, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Heading, Text, IconButton, Tooltip } from 'frosted-ui';
import { QuestionCircle16, Code16 } from '@frosted-ui/icons';
import { useIsMobile } from '../hooks/useIsMobile';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  // New page → start at the top (the router keeps the old scroll position).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-background)',
      }}
    >
      {/* Header */}
      <header
        style={{
          backgroundColor: 'var(--color-panel-solid)',
          borderBottom: '1px solid var(--gray-a6)',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '0 var(--space-4)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-4)',
              height: 64,
            }}
          >
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                textDecoration: 'none',
                justifySelf: 'start',
              }}
            >
              <img
                src="/icon.png"
                alt=""
                style={{ height: isMobile ? 44 : 56, width: isMobile ? 44 : 56 }}
              />
              <div>
                <Heading size="4">Paywall Lab</Heading>
                {!isMobile && (
                  <Text size="1" color="gray">
                    Test your price where your buyers already are
                  </Text>
                )}
              </div>
            </Link>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                justifySelf: 'end',
              }}
            >
              {import.meta.env.DEV && (
                <Tooltip content="Dev: view all tests">
                  <IconButton
                    variant="ghost"
                    color="orange"
                    size="2"
                    onClick={() => navigate('/dev/experiments')}
                    aria-label="Dev: view all tests"
                  >
                    <Code16 />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip content="Replay the intro">
                <IconButton
                  variant="ghost"
                  color="gray"
                  size="2"
                  // Only a look back: nothing is saved, so Home stays Home.
                  onClick={() => navigate('/welcome')}
                  aria-label="Replay the intro"
                >
                  <QuestionCircle16 />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          maxWidth: 1280,
          width: '100%',
          margin: '0 auto',
          padding: 'var(--space-6) var(--space-4)',
        }}
      >
        {children}
      </main>

      {/* Footer */}
      <footer
        style={{
          borderTop: '1px solid var(--gray-a6)',
          backgroundColor: 'var(--color-panel-solid)',
          marginTop: 'auto',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: 'var(--space-5) var(--space-4)',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2) var(--space-4)',
          }}
        >
          <Text size="2" color="gray">
            Paywall Lab — price tests for Whop creators
          </Text>
          <div style={{ display: 'flex', gap: 'var(--space-4)', whiteSpace: 'nowrap' }}>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="pl-tap"
              style={{ textDecoration: 'none' }}
            >
              <Text size="1" color="gray">
                Privacy Policy
              </Text>
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="pl-tap"
              style={{ textDecoration: 'none' }}
            >
              <Text size="1" color="gray">
                Terms of Service
              </Text>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
